import { useMemo, useRef } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { FastField, getIn } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Colors } from "../../../utils/style/colors";
import { SoundIcon } from "../../../utils/icons";
import { selectSelectedDevice } from "../../../redux/selectors/device.selector";
import {
  sortEnumOptions,
  humanizeSettingKey,
  formatDurationLabel,
} from "../utils";
import { getToneAsset, isToneValue } from "../toneAssets";

export default function EnumInput({
  id,
  name,
  schema = {},
  options,
  onChange,
  disabled,
}) {
  const { t } = useTranslation();
  const device = useSelector(selectSelectedDevice());
  const audioRef = useRef(null);
  // Deterministic order (backend order is unstable); humanized label fallback.
  const sortedOptions = useMemo(
    () => sortEnumOptions(options || schema.values || []),
    [options, schema.values]
  );
  const isDuration = schema.inputHelper === "duration_in_seconds";
  const recommended = schema.recommended_value;
  // A tone enum (values like "tone1") gets a preview/play control.
  const isTone = useMemo(() => sortedOptions.some(isToneValue), [sortedOptions]);
  const label = (opt) => {
    let base = isDuration
      ? formatDurationLabel(opt, t)
      : t(opt, { defaultValue: humanizeSettingKey(opt) });
    if (recommended != null && String(opt) === String(recommended)) {
      base += ` (${t("recommended", { defaultValue: "recommended" })})`;
    }
    return base;
  };

  const playTone = (value) => {
    const asset = getToneAsset(value, device?.isTwigP5);
    if (!asset || !audioRef.current) return;
    audioRef.current.src = asset;
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch(() => {});
  };

  return (
    <FastField name={id}>
      {({ field, form }) => {
        const touched = getIn(form.touched, field.name);
        const error = getIn(form.errors, field.name);
        const toneAsset = isTone
          ? getToneAsset(field.value, device?.isTwigP5)
          : null;

        return (
          <Box
            sx={{
              py: 0.75,
              px: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <InputLabel
              id={`${id}-label`}
              sx={{
                flex: 1,
                position: "relative",
                transform: "none",
                color: Colors.black,
                fontSize: "0.95rem",
              }}
            >
              {t(name, { defaultValue: humanizeSettingKey(name) })}
            </InputLabel>
            {isTone && (
              <IconButton
                size="small"
                disabled={!toneAsset}
                onClick={() => playTone(field.value)}
                aria-label={t("playTone", { defaultValue: "Play tone" })}
              >
                <SoundIcon size={0.85} color={Colors.gray60} />
              </IconButton>
            )}
            <FormControl
              size="small"
              sx={{
                width: "fit-content",
                maxWidth: "50%",
                minWidth: "120px",
              }}
              error={Boolean(touched && error)}
              disabled={disabled}
            >
              <Select
                {...field}
                labelId={`${id}-label`}
                value={field.value ?? sortedOptions[0]}
                onChange={(e) => {
                  form.setFieldValue(field.name, e.target.value);
                  onChange?.(field.name, e.target.value);
                  if (isTone) playTone(e.target.value);
                }}
              >
                {sortedOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {label(opt)}
                  </MenuItem>
                ))}
              </Select>

              {touched && error && <FormHelperText>{error}</FormHelperText>}
            </FormControl>
            {isTone && <audio ref={audioRef} style={{ display: "none" }} />}
          </Box>
        );
      }}
    </FastField>
  );
}
